/**
 * InventarioMaster.jsx · Navegador jerárquico 4 niveles:
 *   Sucursal → Bodega → Categoría → Material
 *
 * Migración Ago-2026 (mesa técnica): reemplaza vista plana previa.
 * Cada nivel es una tarjeta clicable que carga el siguiente nivel.
 */
import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Building2, Warehouse, FolderTree, Package,
  ChevronRight, Home, Loader2, Search, RefreshCw,
  Eye, X, Hash, GitCompare,
} from "lucide-react";

const LEVELS = {
  BRANCH: 0,
  WAREHOUSE: 1,
  CATEGORY: 2,
  MATERIAL: 3,
};

/** Separa "TODOS LOS PRODUCTOS / EQUIPOS / PATCH CORD" en { leaf: "PATCH CORD", parents: "TODOS LOS PRODUCTOS / EQUIPOS" } */
const splitCategoryPath = (path) => {
  if (!path) return { leaf: "Sin categoría", parents: "" };
  const parts = path.split("/").map((p) => p.trim()).filter(Boolean);
  if (parts.length <= 1) return { leaf: path, parents: "" };
  return { leaf: parts[parts.length - 1], parents: parts.slice(0, -1).join(" / ") };
};

export default function InventarioMaster() {
  const [level, setLevel] = useState(LEVELS.BRANCH);
  const [branch, setBranch] = useState(null);        // {branch, products, total_onhand}
  const [warehouse, setWarehouse] = useState(null);  // {location_id, name, complete}
  const [category, setCategory] = useState(null);    // {category, products}

  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  // v101.0.0 · Comparar bodega con Odoo / resincronizar
  const [cmp, setCmp] = useState({ open: false, loading: false, syncing: false, data: null, onlyDiff: true });

  const runCompare = async (locationId) => {
    setCmp((p) => ({ ...p, open: true, loading: true, data: null }));
    try {
      const { data } = await api.get("/v1/admin/inventory-odoo/compare", { params: { location_id: locationId } });
      setCmp((p) => ({ ...p, loading: false, data }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo comparar con Odoo");
      setCmp((p) => ({ ...p, loading: false }));
    }
  };

  // v101.1.0 · Sincronización forzada de UNA bodega (y sus sub-ubicaciones) desde Odoo
  const [syncingId, setSyncingId] = useState(null);
  const forceSync = async (w) => {
    if (!w || syncingId) return false;
    const label = w.name || w.location_name || w.email || `#${w.location_id}`;
    setSyncingId(w.location_id || w.email);
    const tId = toast.loading(`Sincronizando ${label} desde Odoo…`);
    try {
      const body = w.location_id ? { location_id: w.location_id } : { email: w.email };
      const { data } = await api.post("/v1/admin/inventory-odoo/resync-location", body, { timeout: 300000 });
      toast.success(`${label} sincronizada · ${data.rows_upserted} productos actualizados · ${data.zeroed} puestos en 0`, { id: tId });
      if (level === LEVELS.WAREHOUSE && branch) loadWarehouses(branch.branch);
      if (warehouse && warehouse.location_id === w.location_id) {
        loadCategories(w.location_id);
        if (category) loadMaterials(w.location_id, category.category);
      }
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `No se pudo sincronizar ${label}`, { id: tId });
      return false;
    } finally {
      setSyncingId(null);
    }
  };

  // Bodega que aún no aparece en AlfaField: sincronizar por ID de ubicación Odoo o correo del técnico
  const [manualSync, setManualSync] = useState("");
  const runManualSync = async () => {
    const v = manualSync.trim();
    if (!v) return;
    const ok = await forceSync(/^\d+$/.test(v) ? { location_id: Number(v) } : { email: v.toLowerCase() });
    if (ok) setManualSync("");
  };

  const runResync = async () => {
    if (!warehouse) return;
    setCmp((p) => ({ ...p, syncing: true }));
    const ok = await forceSync(warehouse);
    setCmp((p) => ({ ...p, syncing: false }));
    if (ok) await runCompare(warehouse.location_id);
  };

  // Modal de series
  const [serialModal, setSerialModal] = useState({ open: false, product: null, serials: [], loading: false });

  // Nivel 1: Sucursales
  const loadBranches = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/branches");
      setBranches(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Error cargando sucursales");
    } finally { setLoading(false); }
  };

  // Nivel 2: Bodegas de una sucursal
  const loadWarehouses = async (branchName) => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/hierarchy", { params: { branch: branchName } });
      // v3.9.12 · El backend puede devolver `mini_bodegas` (nuevo), `mini` o `locations` (legacy).
      // Normalizamos a lista plana asegurando que TODAS las bodegas aparecen (central + mini).
      const list = [];
      const src = Array.isArray(data) ? data : (data.branches || data.items || [data]);
      src.forEach((b) => {
        if (b.central) list.push({ ...b.central, type: "central" });
        const miniList = b.mini_bodegas || b.mini || b.locations || [];
        miniList.forEach((w) => list.push({ ...w, type: w.type || w.kind || "mini" }));
        // caso: la respuesta es una lista plana ya (sin central/mini) — hoja directa
        if (!b.central && miniList.length === 0 && b.location_id) list.push(b);
      });
      // Ordenar: central primero, luego mini por qty_onhand DESC
      list.sort((a, b) => {
        if (a.type === "central" && b.type !== "central") return -1;
        if (a.type !== "central" && b.type === "central") return 1;
        return (b.qty_onhand || 0) - (a.qty_onhand || 0);
      });
      setWarehouses(list);
    } catch (e) {
      toast.error("Error cargando bodegas");
    } finally { setLoading(false); }
  };

  // Nivel 3: Categorías de una bodega
  const loadCategories = async (locationId) => {
    setLoading(true);
    try {
      const { data } = await api.get("/v1/mesa/inventory/warehouse-categories", {
        params: { location_id: locationId },
      });
      setCategories(data.categories || []);
    } catch (e) {
      toast.error("Error cargando categorías");
    } finally { setLoading(false); }
  };

  // Nivel 4: Materiales de una categoría
  const loadMaterials = async (locationId, categoryName, search = "") => {
    setLoading(true);
    try {
      const params = { location_id: locationId, category: categoryName, limit: 300 };
      if (search) params.q = search;
      const { data } = await api.get("/v1/mesa/inventory/warehouse-materials", { params });
      setMaterials(data.items || []);
    } catch (e) {
      toast.error("Error cargando materiales");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadBranches(); /* eslint-disable-next-line */ }, []);

  // Handlers de navegación
  const goToWarehouses = (b) => { setQ(""); setBranch(b); setLevel(LEVELS.WAREHOUSE); loadWarehouses(b.branch); };
  const goToCategories = (w) => { setQ(""); setWarehouse(w); setLevel(LEVELS.CATEGORY); loadCategories(w.location_id); };
  const goToMaterials = (c) => { setQ(""); setCategory(c); setLevel(LEVELS.MATERIAL); loadMaterials(warehouse.location_id, c.category); };

  const goBack = (targetLevel) => {
    setQ("");
    setLevel(targetLevel);
    if (targetLevel <= LEVELS.CATEGORY) setMaterials([]);
    if (targetLevel <= LEVELS.WAREHOUSE) { setCategories([]); setCategory(null); }
    if (targetLevel <= LEVELS.BRANCH) { setWarehouses([]); setWarehouse(null); }
  };

  const filtered = (list, key = "name") => {
    if (!q.trim()) return list;
    const qn = q.trim().toLowerCase();
    return list.filter((x) => JSON.stringify(x).toLowerCase().includes(qn));
  };

  const openSerials = async (material) => {
    setSerialModal({ open: true, product: material, serials: [], loading: true });
    try {
      const { data } = await api.get("/v1/mesa/inventory/product-serials", {
        params: { product_id: material.product_id, location_id: warehouse.location_id },
      });
      setSerialModal((prev) => ({ ...prev, serials: data.serials || [], loading: false }));
    } catch {
      toast.error("Error cargando series");
      setSerialModal((prev) => ({ ...prev, loading: false }));
    }
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="inventario-master-page">
        {/* Header + Breadcrumb */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full mb-2"
                 style={{ background: "#FBFCE0", border: "1px solid #E9EE6E" }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#C4CA12" }}/>
              <span className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: "#6B7000" }}>Inventario · Master</span>
            </div>
            <h1 className="text-3xl font-bold tracking-tight" style={{ color: "#0A3747" }}>Inventario jerárquico</h1>
            <p className="mt-1 text-sm text-gray-500">Sucursal → Bodega → Categoría → Material · datos vivos de <code>master_inventory</code></p>
          </div>
          <div className="flex items-center gap-2">
            {warehouse && level >= LEVELS.CATEGORY && (
              <button
                onClick={() => forceSync(warehouse)}
                disabled={!!syncingId}
                className="neu-btn disabled:opacity-50"
                data-testid="btn-force-sync-warehouse"
                title="Trae ahora desde Odoo el stock de esta bodega y pone en 0 lo que ya no está"
              >
                {syncingId === warehouse.location_id ? <Loader2 size={14} className="animate-spin"/> : <RefreshCw size={14}/>} Sincronizar bodega
              </button>
            )}
            {warehouse && level >= LEVELS.CATEGORY && (
              <button
                onClick={() => runCompare(warehouse.location_id)}
                className="neu-btn"
                data-testid="btn-compare-odoo"
                title="Compara el stock de esta bodega con Odoo y permite resincronizarla"
              >
                <GitCompare size={14}/> Comparar con Odoo
              </button>
            )}
            <button
              onClick={() => { goBack(LEVELS.BRANCH); loadBranches(); }}
              className="neu-btn"
              data-testid="btn-refresh"
            >
              <RefreshCw size={14}/> Refrescar
            </button>
          </div>
        </div>

        {/* v101.1.0 · Sincronización forzada de una bodega que aún no aparece en la lista */}
        <div className="neu-card px-4 py-2.5 flex items-center gap-2 text-xs flex-wrap" data-testid="manual-sync-bar">
          <RefreshCw size={13} className="text-[#0A3747]"/>
          <span className="text-gray-600">Sincronizar una bodega desde Odoo:</span>
          <input
            value={manualSync}
            onChange={(e) => setManualSync(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") runManualSync(); }}
            placeholder="ID de ubicación Odoo o correo del técnico"
            className="flex-1 min-w-[220px] px-2 py-1 rounded-lg border border-gray-200 text-xs"
            data-testid="manual-sync-input"
          />
          <button
            onClick={runManualSync}
            disabled={!!syncingId || !manualSync.trim()}
            className="px-3 py-1 rounded-lg font-semibold bg-[#0A3747] text-white hover:bg-[#0A3747]/90 disabled:opacity-50 flex items-center gap-1"
            data-testid="manual-sync-btn"
          >
            {syncingId && syncingId === (/^\d+$/.test(manualSync.trim()) ? Number(manualSync.trim()) : manualSync.trim().toLowerCase())
              ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Sincronizar
          </button>
        </div>

        {/* Breadcrumb navegable */}
        <div className="neu-card px-4 py-2.5 flex items-center gap-2 text-sm flex-wrap" data-testid="breadcrumb">
          <button onClick={() => goBack(LEVELS.BRANCH)} className="flex items-center gap-1 text-purple-700 hover:underline" data-testid="crumb-root">
            <Home size={14}/> Sucursales
          </button>
          {branch && (
            <>
              <ChevronRight size={12} className="text-gray-300"/>
              <button onClick={() => goBack(LEVELS.WAREHOUSE)} className={`flex items-center gap-1 ${level > LEVELS.WAREHOUSE ? "text-purple-700 hover:underline" : "text-gray-700 font-semibold"}`} data-testid="crumb-branch">
                <Building2 size={13}/> {branch.branch_name || branch.branch}
              </button>
            </>
          )}
          {warehouse && (
            <>
              <ChevronRight size={12} className="text-gray-300"/>
              <button onClick={() => goBack(LEVELS.CATEGORY)} className={`flex items-center gap-1 ${level > LEVELS.CATEGORY ? "text-purple-700 hover:underline" : "text-gray-700 font-semibold"}`} data-testid="crumb-warehouse">
                <Warehouse size={13}/> {warehouse.name || warehouse.location_name || `Bodega #${warehouse.location_id}`}
              </button>
            </>
          )}
          {category && (
            <>
              <ChevronRight size={12} className="text-gray-300"/>
              <div className="flex items-center gap-1 text-gray-700 font-semibold" data-testid="crumb-category">
                <FolderTree size={13}/> {category.category}
              </div>
            </>
          )}
        </div>

        {/* Buscador local */}
        <div className="relative">
          <Search size={14} className="absolute left-3 top-3 text-gray-400"/>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Filtrar en este nivel..."
            className="w-full pl-9 pr-3 py-2 border rounded text-sm"
            data-testid="level-search"
          />
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-purple-600"/></div>
        ) : (
          <>
            {/* NIVEL 1 · Sucursales */}
            {level === LEVELS.BRANCH && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="branches-grid">
                {filtered(branches, "branch").map((b) => (
                  <button
                    key={b.branch}
                    onClick={() => goToWarehouses(b)}
                    data-testid={`branch-${b.branch}`}
                    className="neu-card p-4 text-left hover:shadow-lg transition group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#DBEAFE" }}>
                        <Building2 size={22} className="text-blue-700"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Sucursal</div>
                        <div className="font-bold text-lg truncate" style={{ color: "#0A3747" }}>{b.branch_name || b.branch}</div>
                        <div className="text-[11px] font-mono text-gray-400">{b.branch}</div>
                        <div className="mt-2 text-xs text-gray-600 space-y-0.5">
                          <div>{b.products} productos · {b.locations} bodegas</div>
                          <div>Stock: <b className="tabular-nums">{Number(b.total_onhand || 0).toLocaleString("es-EC")}</b></div>
                        </div>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-purple-500"/>
                    </div>
                  </button>
                ))}
                {branches.length === 0 && !loading && <div className="col-span-full text-center py-12 text-gray-400 text-sm">Sin sucursales en master_inventory</div>}
              </div>
            )}

            {/* NIVEL 2 · Bodegas */}
            {level === LEVELS.WAREHOUSE && (
              <>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] text-gray-500">
                    <b className="text-[#0A3747]">{filtered(warehouses).length}</b> de <b>{warehouses.length}</b> bodegas
                    {branch?.branch && <> · sucursal <b>{branch.branch}</b></>}
                    {q && <> · filtro &quot;<b>{q}</b>&quot;</>}
                  </div>
                  {warehouses.length > 12 && (
                    <div className="text-[10px] text-gray-400 italic">Usa el buscador para acotar la lista</div>
                  )}
                </div>
                <div
                  className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[70vh] overflow-y-auto pr-1"
                  data-testid="warehouses-grid"
                >
                  {filtered(warehouses).map((w) => (
                  <div
                    key={w.location_id}
                    role="button"
                    tabIndex={0}
                    onClick={() => goToCategories(w)}
                    onKeyDown={(e) => { if (e.key === "Enter") goToCategories(w); }}
                    data-testid={`warehouse-${w.location_id}`}
                    className="neu-card p-4 text-left hover:shadow-lg transition group cursor-pointer"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: w.type === "central" ? "#D1FAE5" : "#FEF3C7" }}>
                        <Warehouse size={22} className={w.type === "central" ? "text-emerald-700" : "text-amber-700"}/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400 flex items-center gap-1">
                          Bodega · {w.type || "—"}
                          {w.type === "central" && <span className="ml-1 px-1 rounded bg-emerald-100 text-emerald-800 font-mono">★ CENTRAL</span>}
                        </div>
                        <div className="font-bold text-base truncate" style={{ color: "#0A3747" }}>{w.name || w.location_name || `#${w.location_id}`}</div>
                        <div className="mt-1 text-[11px] text-gray-500 truncate">{w.complete || w.location_complete_name || ""}</div>
                        <div className="mt-2 text-xs text-gray-600">
                          {w.products || w.products_count || "?"} productos · Stock: <b className="tabular-nums">{Number(w.total_onhand || w.qty_onhand || 0).toLocaleString("es-EC")}</b>
                        </div>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); forceSync(w); }}
                          disabled={!!syncingId}
                          className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-semibold border border-[#0A3747]/20 text-[#0A3747] bg-white hover:bg-[#0A3747] hover:text-white transition disabled:opacity-50"
                          data-testid={`btn-force-sync-${w.location_id}`}
                          title="Trae ahora desde Odoo el stock de esta bodega"
                        >
                          {syncingId === w.location_id ? <Loader2 size={12} className="animate-spin"/> : <RefreshCw size={12}/>}
                          {syncingId === w.location_id ? "Sincronizando…" : "Sincronizar con Odoo"}
                        </button>
                      </div>
                      <ChevronRight size={16} className="text-gray-300 group-hover:text-purple-500"/>
                    </div>
                  </div>
                ))}
                {warehouses.length === 0 && <div className="col-span-full text-center py-8 text-gray-400 text-sm">Sin bodegas en {branch?.branch}</div>}
                {warehouses.length > 0 && filtered(warehouses).length === 0 && <div className="col-span-full text-center py-8 text-gray-400 text-sm">Ninguna bodega coincide con &quot;{q}&quot;</div>}
                </div>
              </>
            )}

            {/* NIVEL 3 · Categorías */}
            {level === LEVELS.CATEGORY && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="categories-grid">
                {filtered(categories, "category").map((c) => {
                  const { leaf, parents } = splitCategoryPath(c.category);
                  return (
                  <button
                    key={c.category}
                    onClick={() => goToMaterials(c)}
                    data-testid={`category-${c.category.replace(/\s/g,'-')}`}
                    className="neu-card p-3 text-left hover:shadow-lg transition group"
                    title={c.category}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#EDE9FE" }}>
                        <FolderTree size={18} className="text-purple-700"/>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Categoría</div>
                        <div className="font-bold text-sm break-words" style={{ color: "#0A3747" }}>{leaf}</div>
                        {parents && <div className="text-[10px] text-gray-400 break-words mt-0.5">{parents}</div>}
                        <div className="mt-1 text-[11px] text-gray-600">
                          {c.products} productos · Stock <b className="tabular-nums">{Number(c.qty_onhand || 0).toLocaleString("es-EC")}</b>
                        </div>
                      </div>
                      <ChevronRight size={14} className="text-gray-300 group-hover:text-purple-500"/>
                    </div>
                  </button>
                  );
                })}
                {categories.length === 0 && <div className="col-span-full text-center py-8 text-gray-400 text-sm">Sin categorías en esta bodega</div>}
              </div>
            )}

            {/* NIVEL 4 · Materiales */}
            {level === LEVELS.MATERIAL && (
              <div className="neu-card overflow-hidden" data-testid="materials-table">
                <div className="p-3 border-b bg-gray-50 flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">
                    {materials.length} materiales en {category?.category}
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-gray-500 font-bold">Código</th>
                        <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-gray-500 font-bold">Material</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-gray-500 font-bold">On-hand</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-gray-500 font-bold">Disponible</th>
                        <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-gray-500 font-bold">Reservado</th>
                        <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-gray-500 font-bold">Asignación</th>
                        <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest text-gray-500 font-bold">Series</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered(materials, "product_name").map((m) => (
                        <tr key={m.product_id} className="border-t hover:bg-purple-50/30" data-testid={`mat-${m.product_id}`}>
                          <td className="px-3 py-2 font-mono text-xs text-gray-500">{m.product_code || "—"}</td>
                          <td className="px-3 py-2 text-sm flex items-center gap-2">
                            <Package size={12} className="text-gray-400 flex-shrink-0"/>
                            <span className="truncate">{m.product_name}</span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums font-mono">{Number(m.qty_onhand || 0).toLocaleString("es-EC")}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-mono text-emerald-700">{Number(m.qty_available || 0).toLocaleString("es-EC")}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-mono text-amber-700">{Number(m.qty_reserved || 0).toLocaleString("es-EC")}</td>
                          <td className="px-3 py-2 text-left text-xs text-gray-500 whitespace-nowrap">
                            {m.in_date ? new Date(m.in_date).toLocaleString("es-EC", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {/* v99.5.1 · Mostrar ícono si tracking=serial O serial_numbers no vacío (fallback) */}
                            {(m.tracking === "serial" || (m.serial_numbers && m.serial_numbers.trim())) && Number(m.qty_onhand || 0) > 0 ? (
                              <button
                                onClick={() => openSerials(m)}
                                className="inline-flex items-center justify-center w-7 h-7 rounded-lg hover:bg-blue-100 text-gray-400 hover:text-blue-700 transition"
                                title="Ver series/lotes"
                                data-testid={`btn-serials-${m.product_id}`}
                              >
                                <Eye size={14} />
                              </button>
                            ) : (m.tracking === "serial" || (m.serial_numbers && m.serial_numbers.trim())) && Number(m.qty_onhand || 0) === 0 ? (
                              <span className="text-[9px] text-gray-300" title="Stock 0">—</span>
                            ) : (
                              <span className="text-[9px] text-gray-300">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {materials.length === 0 && (
                        <tr><td colSpan={7} className="py-8 text-center text-gray-400 text-sm">Sin materiales en {category?.category}</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ═══ Modal de Series/Lotes ═══ */}
      {serialModal.open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="serial-modal-backdrop" onClick={() => setSerialModal({ open: false, product: null, serials: [], loading: false })}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="serial-modal">
            {/* Header */}
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Series / Lotes</div>
                <div className="font-bold text-base mt-0.5" style={{ color: "#0A3747" }}>
                  {serialModal.product?.product_name || "—"}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  Código: <span className="font-mono">{serialModal.product?.product_code || "—"}</span>
                  {warehouse && <> · Bodega: {warehouse.name || warehouse.location_name || `#${warehouse.location_id}`}</>}
                </div>
              </div>
              <button onClick={() => setSerialModal({ open: false, product: null, serials: [], loading: false })} className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-700" data-testid="serial-modal-close">
                <X size={18} />
              </button>
            </div>
            {/* Body */}
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {serialModal.loading ? (
                <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-purple-600" /></div>
              ) : serialModal.serials.length === 0 ? (
                <div className="text-center py-10">
                  <Hash size={28} className="mx-auto text-gray-300 mb-2" />
                  <div className="text-sm font-semibold text-gray-500">
                    {Number(serialModal.product?.qty_onhand || 0) > 0
                      ? "Series no sincronizadas desde Odoo"
                      : "Sin series registradas"}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    {Number(serialModal.product?.qty_onhand || 0) > 0
                      ? "El producto tiene stock, pero los números de serie aún no se han sincronizado."
                      : "Este producto no tiene lotes/números de serie en esta bodega."}
                  </div>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-gray-500 font-bold">#</th>
                      <th className="px-3 py-2 text-left text-[10px] uppercase tracking-widest text-gray-500 font-bold">Número de Serie</th>
                      {serialModal.serials.some((s) => s.qty != null) && (
                        <th className="px-3 py-2 text-right text-[10px] uppercase tracking-widest text-gray-500 font-bold">Qty</th>
                      )}
                      {serialModal.serials.some((s) => s.is_already_invoiced != null) && (
                        <th className="px-3 py-2 text-center text-[10px] uppercase tracking-widest text-gray-500 font-bold">Facturado</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {serialModal.serials.map((s, idx) => (
                      <tr key={s.serial_name + idx} className="border-t hover:bg-blue-50/30" data-testid={`serial-row-${idx}`}>
                        <td className="px-3 py-1.5 text-xs text-gray-400 tabular-nums">{idx + 1}</td>
                        <td className="px-3 py-1.5 font-mono text-xs font-semibold" style={{ color: "#0A3747" }}>{s.serial_name}</td>
                        {serialModal.serials.some((ss) => ss.qty != null) && (
                          <td className="px-3 py-1.5 text-right tabular-nums font-mono text-xs">{s.qty != null ? Number(s.qty).toLocaleString("es-EC") : "—"}</td>
                        )}
                        {serialModal.serials.some((ss) => ss.is_already_invoiced != null) && (
                          <td className="px-3 py-1.5 text-center text-xs">{s.is_already_invoiced ? "Sí" : "No"}</td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {/* Footer */}
            <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between text-xs text-gray-500">
              <span>{serialModal.serials.length} serie(s) encontrada(s)</span>
              <button onClick={() => setSerialModal({ open: false, product: null, serials: [], loading: false })} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#0A3747] text-white hover:bg-[#0A3747]/90" data-testid="serial-modal-close-btn">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v101.0.0 · Modal Comparar con Odoo */}
      {cmp.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="compare-odoo-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            <div className="px-5 py-3 border-b flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-widest font-bold text-gray-400">Inventario vs Odoo</div>
                <div className="font-bold truncate" style={{ color: "#0A3747" }}>
                  {cmp.data?.location?.complete_name || warehouse?.name || warehouse?.location_name || `Bodega #${warehouse?.location_id}`}
                </div>
                {cmp.data?.technicians?.length > 0 && (
                  <div className="text-[11px] text-gray-500 truncate">Técnico(s): {cmp.data.technicians.map((t) => t.name || t.email).join(", ")}</div>
                )}
              </div>
              <button onClick={() => setCmp((p) => ({ ...p, open: false }))} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="compare-odoo-close">
                <X size={16}/>
              </button>
            </div>
            <div className="p-5 overflow-auto flex-1 space-y-3">
              {cmp.loading && (
                <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 size={16} className="animate-spin"/> Consultando Odoo…</div>
              )}
              {!cmp.loading && cmp.data && (() => {
                const S = {
    falta_en_alfafield: { label: "Falta en AlfaField", cls: "bg-red-100 text-red-700" },
    cantidad_distinta: { label: "Cantidad distinta", cls: "bg-amber-100 text-amber-700" },
    sobra_en_alfafield: { label: "Ya no está en Odoo", cls: "bg-purple-100 text-purple-700" },
    ok: { label: "OK", cls: "bg-green-100 text-green-700" },
  };
                const sm = cmp.data.summary || {};
                const rows = (cmp.data.items || []).filter((i) => !cmp.onlyDiff || i.status !== "ok");
                const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString("es-EC"));
                return (
                  <>
                    <div className="flex flex-wrap gap-2 text-xs" data-testid="compare-odoo-summary">
                      {Object.keys(S).map((k) => (
                        <span key={k} className={`px-2 py-1 rounded-full font-semibold ${S[k].cls}`}>{S[k].label}: {sm[k] || 0}</span>
                      ))}
                    </div>
                    {(cmp.data.notes || []).map((n, i) => (
                      <div key={i} className="text-xs px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">{n}</div>
                    ))}
                    <label className="flex items-center gap-2 text-xs text-gray-600">
                      <input type="checkbox" checked={cmp.onlyDiff} onChange={(e) => setCmp((p) => ({ ...p, onlyDiff: e.target.checked }))}/>
                      Mostrar solo diferencias
                    </label>
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-2 text-left">Producto</th>
                          <th className="px-2 py-2 text-right">Odoo a mano</th>
                          <th className="px-2 py-2 text-right">Odoo disponible</th>
                          <th className="px-2 py-2 text-right">AlfaField</th>
                          <th className="px-2 py-2 text-left">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((it) => (
                          <tr key={`${it.product_id}-${it.location_id}`} className="border-t" data-testid={`compare-row-${it.product_id}`}>
                            <td className="px-2 py-1.5">
                              <div className="font-semibold" style={{ color: "#0A3747" }}>{it.code ? `[${it.code}] ` : ""}{it.name}</div>
                            </td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{fmt(it.odoo_onhand)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{fmt(it.odoo_available)}</td>
                            <td className="px-2 py-1.5 text-right tabular-nums">{fmt(it.af_onhand)}</td>
                            <td className="px-2 py-1.5"><span className={`px-2 py-0.5 rounded-full font-semibold ${S[it.status].cls}`}>{S[it.status].label}</span></td>
                          </tr>
                        ))}
                        {rows.length === 0 && (
                          <tr><td colSpan={5} className="px-2 py-6 text-center text-gray-400">Sin diferencias: AlfaField coincide con Odoo.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </>
                );
              })()}
            </div>
            <div className="px-5 py-3 border-t bg-gray-50 flex items-center justify-between gap-2 text-xs">
              <span className="text-gray-500">Trae de Odoo solo esta bodega y pone en 0 lo que ya no está.</span>
              <div className="flex gap-2">
                <button onClick={() => runCompare(warehouse.location_id)} disabled={cmp.loading || cmp.syncing} className="px-3 py-1.5 font-semibold rounded-lg border bg-white hover:bg-gray-100 disabled:opacity-50" data-testid="compare-odoo-refresh">
                  Volver a comparar
                </button>
                <button onClick={runResync} disabled={cmp.loading || cmp.syncing} className="px-3 py-1.5 font-semibold rounded-lg bg-[#0A3747] text-white hover:bg-[#0A3747]/90 disabled:opacity-50 flex items-center gap-1" data-testid="compare-odoo-resync">
                  {cmp.syncing ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>} Resincronizar bodega
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
}
